import { getCurrentStoreConfig } from "@app/config"

import { session } from "./session"

/**
 * Searchspring Beacon 2.0 client
 * ---
 * Posts events to https://analytics.searchspring.net/beacon/v2/{siteId}/{module}/{event}
 *
 * Uses `text/plain` content-type to avoid CORS preflight (OPTIONS) requests and
 * prefers `navigator.sendBeacon` for fire-and-forget delivery so events survive
 * page navigation (critical for clickthrough and cart events).
 *
 * Reference:
 *   https://docs.searchspring.com/reference/beacon-tracking
 *   https://docs.searchspring.com/reference/context-object-guide
 */

const BEACON_BASE = "https://analytics.searchspring.net/beacon/v2"
const INITIATOR = "dotcollective/viktoria-woods/1.0"

export type BeaconModule =
  | "search"
  | "category"
  | "autocomplete"
  | "recommendations"
  | "cart"
  | "product"
  | "shopper"
  | "order"

export type BeaconProductResult = {
  type?: "product" | "banner"
  parentId: string
  uid: string
  sku?: string
  qty?: number
  price?: number
}

export type BeaconBanner = { uid: string }

export type BeaconContext = {
  timestamp: string
  pageUrl: string
  userId: string
  sessionId: string
  pageLoadId: string
  initiator: string
  shopperId?: string
  currency?: { code: string }
  userAgent?: string
  dev?: boolean
}

const DEV_FLAG_KEY = "ssDev"
const DEV_QUERY_PARAM = "ss_dev"

function readSsDevCookie(): boolean {
  if (typeof document === "undefined") return false
  const match = document.cookie.match(/(?:^|; )ssDev=([^;]*)/)
  return !!match && decodeURIComponent(match[1]) === "1"
}

function writeSsDevCookie(value: boolean) {
  if (typeof document === "undefined") return
  if (value) {
    // 1 day is enough for a QA session; persists across the storefront and
    // the order/transaction pixel which lives on the order status page.
    document.cookie = "ssDev=1; path=/; max-age=86400; SameSite=Lax"
  } else {
    document.cookie = "ssDev=; path=/; max-age=0; SameSite=Lax"
  }
}

/**
 * Sandbox mode toggle. When on, every event is sent with `context.dev = true`,
 * which Searchspring uses to exclude the request from production reports while
 * still validating the payload. Three ways to flip it on:
 *   - append `?ss_dev=1` to any storefront URL (writes the cookie)
 *   - append `?ss_dev=0` to clear the cookie
 *   - set `localStorage.ssDev = "1"` in DevTools (per-tab override)
 * The pixel reads the same `ssDev` cookie on the order status page.
 */
function isDevMode(): boolean {
  try {
    if (typeof window === "undefined") return false
    const params = new URLSearchParams(window.location.search)
    const fromUrl = params.get(DEV_QUERY_PARAM)
    if (fromUrl === "1") {
      writeSsDevCookie(true)
      return true
    }
    if (fromUrl === "0") {
      writeSsDevCookie(false)
      return false
    }
    if (
      typeof localStorage !== "undefined" &&
      localStorage.getItem(DEV_FLAG_KEY) === "1"
    ) {
      return true
    }
    return readSsDevCookie()
  } catch {
    /* sandboxed contexts may throw on storage / location access */
  }
  return false
}

export function getSiteId() {
  const storeConfig = getCurrentStoreConfig()
  return storeConfig.searchspring.products
}

export function buildContext(): BeaconContext {
  const shopperId = window.SHOPIFY_DATA?.customer?.id
  const currencyCode = window.SHOPIFY_DATA?.currency?.currencyCode

  const context: BeaconContext = {
    timestamp: new Date().toISOString(),
    pageUrl: window.location.href,
    userId: String(session.userId.value ?? ""),
    sessionId: String(session.sessionId.value ?? ""),
    pageLoadId: String(session.pageLoadId.value ?? ""),
    initiator: INITIATOR,
  }

  if (shopperId !== undefined && shopperId !== null && shopperId !== "") {
    context.shopperId = String(shopperId)
  }

  if (currencyCode) {
    context.currency = { code: currencyCode }
  }

  if (isDevMode()) {
    context.dev = true
  }

  return context
}

function endpoint(module: BeaconModule, event: string) {
  return `${BEACON_BASE}/${getSiteId()}/${module}/${event}`
}

/**
 * Send a single event to Beacon 2.0.
 * Uses sendBeacon when available (handles unload/navigation), otherwise
 * falls back to fetch with keepalive so the request survives page changes.
 */
export function sendEvent(
  module: BeaconModule,
  event: string,
  data?: Record<string, unknown>
) {
  try {
    const payload = JSON.stringify({
      context: buildContext(),
      ...(data ? { data } : {}),
    })
    const url = endpoint(module, event)

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "text/plain" })
      const sent = navigator.sendBeacon(url, blob)
      if (sent) return
    }

    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* swallow network errors - beacon is best-effort */
    })
  } catch {
    /* Never let analytics break the site */
  }
}

/**
 * Results tracking events (search / category / autocomplete / recommendations).
 *
 * Shopping platform events (product/pageview, cart/add, cart/remove,
 * shopper/login, order/transaction) are handled by the Searchspring Shopify
 * Customer Events pixel installed by the merchant via the SS app, so they
 * do not live in this storefront client. Re-add helpers here if that pixel
 * is ever disconnected.
 */

type ResultsModule = "search" | "category" | "autocomplete" | "recommendations"

export function trackRender(
  module: ResultsModule,
  responseId: string,
  extra?: { tag?: string }
) {
  if (!responseId) return
  sendEvent(module, "render", {
    responseId,
    ...(extra?.tag ? { tag: extra.tag } : {}),
  })
}

export function trackImpression(
  module: ResultsModule,
  responseId: string,
  results: BeaconProductResult[],
  banners: BeaconBanner[] = [],
  extra?: { tag?: string }
) {
  if (!responseId || !results.length) return
  sendEvent(module, "impression", {
    responseId,
    results,
    banners,
    ...(extra?.tag ? { tag: extra.tag } : {}),
  })
}

export function trackClickthrough(
  module: ResultsModule,
  responseId: string,
  result: BeaconProductResult,
  extra?: { tag?: string }
) {
  if (!responseId) return
  sendEvent(module, "clickthrough", {
    responseId,
    results: [result],
    ...(extra?.tag ? { tag: extra.tag } : {}),
  })
}

export function trackRedirect(
  module: "search" | "autocomplete",
  responseId: string,
  redirect: string
) {
  if (!responseId || !redirect) return
  sendEvent(module, "redirect", { responseId, redirect })
}
